import { useState, useEffect } from 'react';
import api from '../../services/api';
import { TrendingUp, FileText, Clock, Calendar, X, AlertCircle, Fuel, CreditCard, ArrowLeft, Settings, Camera, Map, Route as RouteIcon, Receipt } from 'lucide-react';
import { format, addMonths, startOfMonth, endOfMonth, differenceInDays, nextMonday, setDate } from 'date-fns';
import BottomBar from '@/components/BottomBar';
import { useDropzone } from 'react-dropzone';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/mileage`;

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [showMileageUpload, setShowMileageUpload] = useState(false);
  const [mileageTiming, setMileageTiming] = useState(null); // 'start' or 'end'
  const [showReceiptUpload, setShowReceiptUpload] = useState(false);
  const [showTimerModal, setShowTimerModal] = useState(false);
  const [showFuelCard, setShowFuelCard] = useState(false); // For fuel card visibility
  const [fuelMileageSinceLastFill, setFuelMileageSinceLastFill] = useState(0);
  
  // Fuel card details (in production, fetch from user profile)
  const [fuelCardDetails] = useState({
    vehicleReg: 'VX24 ZBW',
    fuelCardPin: '1234'
  });
  
  const [previousMonthData, setPreviousMonthData] = useState({
    total: 0,
    business: 0,
    personal: 0
  });
  
  const [currentMonthData, setCurrentMonthData] = useState({
    total: 0,
    business: 0,
    personal: 0
  });
  
  const [receiptData, setReceiptData] = useState({
    count: 0,
    totalSpent: 0
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const now = new Date();
      const currentMonth = format(now, 'yyyy-MM');
      const previousMonth = format(addMonths(now, -1), 'yyyy-MM');
      
      // Fetch previous month mileage
      const prevMonthMileage = await api.get(`${API}/monthly-mileage/${previousMonth}`).catch(() => ({ data: null }));
      if (prevMonthMileage.data) {
        setPreviousMonthData({
          total: prevMonthMileage.data.total_miles,
          business: prevMonthMileage.data.business_miles,
          personal: prevMonthMileage.data.personal_miles
        });
      }
      
      // Fetch current month mileage
      const currMonthMileage = await api.get(`${API}/monthly-mileage/${currentMonth}`).catch(() => ({ data: null }));
      if (currMonthMileage.data) {
        setCurrentMonthData({
          total: currMonthMileage.data.total_miles,
          business: currMonthMileage.data.business_miles,
          personal: currMonthMileage.data.personal_miles
        });
      }
      
      // Fetch receipts
      const receipts = await api.get(`${API}/receipts`);
      const totalSpent = receipts.data.reduce((sum, receipt) => sum + receipt.amount, 0);
      setReceiptData({
        count: receipts.data.length,
        totalSpent: totalSpent
      });
      
      // Fetch fuel card mileage (since last fill)
      const fuelCardData = await api.get(`${API}/fuel-card`).catch(() => ({ data: null }));
      if (fuelCardData.data && fuelCardData.data.last_fill_mileage) {
        setFuelMileageSinceLastFill(fuelCardData.data.last_fill_mileage);
      }
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate deadlines
  const getDeadlines = () => {
    const now = new Date();
    const nextMonth = addMonths(startOfMonth(now), 1);
    
    // First Monday of next month
    const firstMonday = nextMonday(addMonths(endOfMonth(now), 0));
    const daysToFirstMonday = differenceInDays(firstMonday, now);
    
    // 9th of next month
    const ninthOfNextMonth = setDate(nextMonth, 9);
    const daysToNinth = differenceInDays(ninthOfNextMonth, now);
    
    return {
      firstMonday,
      daysToFirstMonday,
      ninthOfNextMonth,
      daysToNinth
    };
  };

  const deadlines = getDeadlines();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-[#14B8A6] text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden" style={{ backgroundColor: '#f5f5f5' }}>
      {/* Clickable Month Selector Bar at Top */}
      <div className="flex-none w-full" style={{ backgroundColor: '#e8e8e8', borderBottom: '2px solid #ee7203' }}>
        <div className="max-w-md mx-auto px-4 py-1.5 flex items-center justify-between">
          <button
            onClick={() => {
              const newDate = addMonths(selectedMonth, -1);
              setSelectedMonth(newDate);
            }}
            className="p-1 hover:bg-gray-300 rounded-full transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
          <button
            onClick={() => {
              // TODO: Show date picker modal
              console.log('Open date picker');
            }}
            className="text-center hover:bg-gray-300 px-3 py-1 rounded-lg transition-colors"
          >
            <span className="text-sm font-bold text-gray-800">
              {format(selectedMonth, 'MMMM yyyy')}
            </span>
          </button>
          <button
            onClick={() => {
              const newDate = addMonths(selectedMonth, 1);
              setSelectedMonth(newDate);
            }}
            className="p-1 hover:bg-gray-300 rounded-full transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-700 rotate-180" />
          </button>
        </div>
      </div>

      {/* Main Content with Cards - Add bottom padding to clear the menu bars */}
      <div className="flex-1 w-full px-2 py-2 overflow-y-auto" style={{ paddingBottom: '140px' }}>
        {/* 2x3 Grid for 6 cards */}
        <div className="grid grid-cols-2 gap-2 max-w-md mx-auto">
          
          {/* Previous Month Card */}
          <button
            onClick={() => navigate('/mileage-tracker/monthly')}
            data-testid="previous-month-card"
            className="p-2.5 text-left transition-all hover:scale-105 w-full rounded-2xl shadow-sm flex flex-col"
            style={{ 
              backgroundColor: '#e8e8e8',
              border: '2px solid #ee7203',
              minHeight: '140px'
            }}
          >
            <div className="flex items-center gap-1 mb-1">
              <Calendar className="w-4 h-4 text-[#8B5CF6]" />
              <h3 className="text-xs font-bold text-gray-800">Prev Month</h3>
            </div>
            <p className="text-[10px] text-gray-600 mb-1">{format(addMonths(new Date(), -1), 'MMM')}</p>
            <div className="space-y-1 mt-auto">
              <div>
                <p className="text-[10px] text-gray-600">Total</p>
                <p className="text-base font-bold text-[#8B5CF6]">{previousMonthData.total.toFixed(0)} mi</p>
              </div>
              <div className="grid grid-cols-2 gap-1 text-[9px]">
                <div>
                  <p className="text-gray-600">Bus</p>
                  <p className="font-bold text-[#ee7203]">{previousMonthData.business.toFixed(0)}</p>
                </div>
                <div>
                  <p className="text-gray-600">Per</p>
                  <p className="font-bold text-[#06B6D4]">{previousMonthData.personal.toFixed(0)}</p>
                </div>
              </div>
            </div>
          </button>

          {/* Current Month Card */}
          <button
            onClick={() => navigate('/mileage-tracker/monthly')}
            data-testid="current-month-card"
            className="p-2.5 text-left transition-all hover:scale-105 w-full rounded-2xl shadow-sm flex flex-col"
            style={{ 
              backgroundColor: '#e8e8e8',
              border: '2px solid #ee7203',
              minHeight: '140px'
            }}
          >
            <div className="flex items-center gap-1 mb-1">
              <TrendingUp className="w-4 h-4 text-[#ee7203]" />
              <h3 className="text-xs font-bold text-gray-800">This Month</h3>
            </div>
            <p className="text-[10px] text-gray-600 mb-1">{format(new Date(), 'MMM')}</p>
            <div className="space-y-1 mt-auto">
              <div>
                <p className="text-[10px] text-gray-600">Total</p>
                <p className="text-base font-bold text-[#ee7203]">{currentMonthData.total.toFixed(0)} mi</p>
              </div>
              <div className="grid grid-cols-2 gap-1 text-[9px]">
                <div>
                  <p className="text-gray-600">Bus</p>
                  <p className="font-bold text-[#ee7203]">{currentMonthData.business.toFixed(0)}</p>
                </div>
                <div>
                  <p className="text-gray-600">Per</p>
                  <p className="font-bold text-[#06B6D4]">{currentMonthData.personal.toFixed(0)}</p>
                </div>
              </div>
            </div>
          </button>

          {/* Fuel Mileage Since Last Fill Card */}
          <button
            onClick={() => navigate('/mileage-tracker/trips')}
            className="p-2.5 text-left transition-all hover:scale-105 w-full rounded-2xl shadow-sm flex flex-col"
            style={{ 
              backgroundColor: '#e8e8e8',
              border: '2px solid #ee7203',
              minHeight: '140px'
            }}
          >
            <div className="flex items-center gap-1 mb-1">
              <Fuel className="w-4 h-4 text-[#10B981]" />
              <h3 className="text-xs font-bold text-gray-800">Last Fill</h3>
            </div>
            <div className="space-y-1 mt-auto">
              <div>
                <p className="text-[10px] text-gray-600">Miles</p>
                <p className="text-base font-bold text-[#10B981]">{fuelMileageSinceLastFill.toFixed(0)} mi</p>
              </div>
              <p className="text-[9px] text-gray-500">Last: No data</p>
            </div>
          </button>

          {/* Fuel Card Details Card */}
          <button
            onClick={() => setShowFuelCard(!showFuelCard)}
            className="p-2.5 text-left transition-all hover:scale-105 w-full rounded-2xl shadow-sm flex flex-col"
            style={{ 
              backgroundColor: '#e8e8e8',
              border: '2px solid #ee7203',
              minHeight: '140px'
            }}
          >
            <div className="flex items-center gap-1 mb-1">
              <CreditCard className="w-4 h-4 text-[#3B82F6]" />
              <h3 className="text-xs font-bold text-gray-800">Fuel Card</h3>
            </div>
            <div className="space-y-1 mt-auto">
              <div>
                <p className="text-[10px] text-gray-600">Reg</p>
                <p className="text-sm font-bold text-gray-800">{fuelCardDetails.vehicleReg}</p>
              </div>
              <div>
                <p className="text-[10px] text-gray-600">PIN</p>
                <p className="text-sm font-bold text-[#3B82F6]">
                  {showFuelCard ? fuelCardDetails.fuelCardPin : '••••'}
                </p>
              </div>
            </div>
          </button>

          {/* Receipts Card */}
          <button
            onClick={() => navigate('/mileage-tracker/receipts')}
            data-testid="receipts-card"
            className="p-2.5 text-left transition-all hover:scale-105 w-full rounded-2xl shadow-sm flex flex-col"
            style={{ 
              backgroundColor: '#e8e8e8',
              border: '2px solid #ee7203',
              minHeight: '140px'
            }}
          >
            <div className="flex items-center gap-1 mb-1">
              <FileText className="w-4 h-4 text-[#ee7203]" />
              <h3 className="text-xs font-bold text-gray-800">Receipts</h3>
            </div>
            <div className="space-y-1 mt-auto">
              <div>
                <p className="text-[10px] text-gray-600">Spent</p>
                <p className="text-base font-bold text-[#ee7203]">£{receiptData.totalSpent.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-[10px] text-gray-600">{receiptData.count} uploaded</p>
              </div>
            </div>
          </button>

          {/* Deadlines Card */}
          <button
            onClick={() => setShowTimerModal(true)}
            data-testid="deadlines-card"
            className="p-2.5 text-left transition-all hover:scale-105 w-full rounded-2xl shadow-sm flex flex-col"
            style={{ 
              backgroundColor: '#e8e8e8',
              border: '2px solid #ee7203',
              minHeight: '140px'
            }}
          >
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-5 h-5 md:w-6 md:h-6 text-[#F59E0B]" />
                <h3 className="text-sm md:text-base font-bold">Deadlines</h3>
              </div>
            </div>
            <div className="space-y-3 mt-auto">
              <div>
                <p className="text-xs md:text-sm font-bold text-[#F59E0B]">
                  {format(deadlines.firstMonday, 'MMM d, yyyy')}
                </p>
                <p className="text-xs text-gray-400">{deadlines.daysToFirstMonday} days</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Final Date</p>
                <p className="text-xs md:text-sm font-bold text-red-400">
                  {format(deadlines.ninthOfNextMonth, 'MMM d, yyyy')}
                </p>
                <p className="text-xs text-gray-400">{deadlines.daysToNinth} days</p>
              </div>
            </div>
          </button>
        </div>
      </div>
      
      {/* Timer Modal */}
      {showTimerModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="glass-card p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-6 h-6 text-[#F59E0B]" />
                <h2 className="text-xl font-bold">Submission Deadlines</h2>
              </div>
              <button
                onClick={() => setShowTimerModal(false)}
                data-testid="close-timer-modal"
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-[rgba(245,158,11,0.1)] border border-[rgba(245,158,11,0.3)] rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-[#F59E0B] mt-0.5" />
                  <div>
                    <p className="font-bold text-[#F59E0B] mb-1">Start Date</p>
                    <p className="text-sm text-gray-300 mb-2">
                      {format(deadlines.firstMonday, 'EEEE, MMMM d, yyyy')}
                    </p>
                    <p className="text-sm text-gray-400">
                      Mileage submissions open on the first Monday of each month
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-[rgba(239,68,68,0.1)] border border-[rgba(239,68,68,0.3)] rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-400 mt-0.5" />
                  <div>
                    <p className="font-bold text-red-400 mb-1">Final Deadline</p>
                    <p className="text-sm text-gray-300 mb-2">
                      {format(deadlines.ninthOfNextMonth, 'EEEE, MMMM d, yyyy')}
                    </p>
                    <p className="text-sm text-gray-400">
                      Mileage must be submitted by the 9th of the month
                    </p>
                    <p className="text-sm font-bold text-red-400 mt-2">
                      ⚠️ Late submissions incur a £50 charge
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={() => setShowTimerModal(false)}
              className="btn-primary w-full mt-6"
            >
              Got it
            </button>
          </div>
        </div>
      )}
      
      {/* Mileage Upload Modal */}
      {showMileageUpload && (
        <MileageUploadModal 
          onClose={() => {
            setShowMileageUpload(false);
            setMileageTiming(null);
          }} 
          initialTiming={mileageTiming}
        />
      )}
      
      {/* Receipt Upload Modal */}
      {showReceiptUpload && (
        <ReceiptUploadModal onClose={() => setShowReceiptUpload(false)} />
      )}
    </div>
  );
};

// Mileage Upload Modal Component
const MileageUploadModal = ({ onClose, initialTiming }) => {
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [mileageType, setMileageType] = useState(initialTiming || ''); // 'start' or 'end'
  const [uploading, setUploading] = useState(false);

  const onDrop = async (acceptedFiles) => {
    if (acceptedFiles.length === 0 || !mileageType) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', acceptedFiles[0]);

    try {
      const response = await api.post(`${API}/ocr/extract-mileage`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      const mileageValue = response.data.start_mileage || response.data.end_mileage || 0;
      
      // Save to monthly mileage
      const monthData = await api.get(`${API}/monthly-mileage/${selectedMonth}`).catch(() => ({ data: null }));
      
      const payload = {
        month: selectedMonth,
        start_mileage: mileageType === 'start' ? mileageValue : (monthData.data?.start_mileage || mileageValue),
        end_mileage: mileageType === 'end' ? mileageValue : (monthData.data?.end_mileage || mileageValue),
      };
      
      await api.post(`${API}/monthly-mileage`, payload);
      toast.success(`${mileageType === 'start' ? 'Start' : 'End'} mileage saved: ${mileageValue}`);
      onClose();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to process image');
    } finally {
      setUploading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.heic', '.heif'] },
    multiple: false,
    disabled: !mileageType,
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="glass-card p-6 max-w-md w-full">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold">Upload Dashboard Photo</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Select Month</label>
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="glass-input w-full text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Mileage Type</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setMileageType('start')}
                className={`p-3 rounded-lg border-2 transition-colors ${
                  mileageType === 'start'
                    ? 'border-[#14B8A6] bg-[rgba(20,184,166,0.2)]'
                    : 'border-[rgba(20,184,166,0.3)]'
                }`}
              >
                <p className="font-medium">Start of Month</p>
              </button>
              <button
                onClick={() => setMileageType('end')}
                className={`p-3 rounded-lg border-2 transition-colors ${
                  mileageType === 'end'
                    ? 'border-[#14B8A6] bg-[rgba(20,184,166,0.2)]'
                    : 'border-[rgba(20,184,166,0.3)]'
                }`}
              >
                <p className="font-medium">End of Month</p>
              </button>
            </div>
          </div>

          {mileageType && (
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-xl p-8 transition-colors cursor-pointer ${
                isDragActive
                  ? 'border-[#14B8A6] bg-[rgba(20,184,166,0.1)]'
                  : 'border-[rgba(20,184,166,0.3)]'
              }`}
            >
              <input {...getInputProps()} />
              {uploading ? (
                <div className="text-center">
                  <div className="animate-spin w-12 h-12 border-4 border-[#14B8A6] border-t-transparent rounded-full mx-auto mb-4"></div>
                  <p className="text-gray-300">Processing image...</p>
                </div>
              ) : (
                <div className="text-center">
                  <p className="text-lg font-medium mb-2">
                    {isDragActive ? 'Drop image here' : 'Click or drag to upload'}
                  </p>
                  <p className="text-sm text-gray-400">Upload dashboard photo showing mileage</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Receipt Upload Modal Component
const ReceiptUploadModal = ({ onClose }) => {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [amount, setAmount] = useState('');
  const [merchant, setMerchant] = useState('');
  const [notes, setNotes] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [scanning, setScanning] = useState(false);

  const scanReceipt = async (file) => {
    setScanning(true);
    toast.info('Scanning receipt...');
    
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const base64 = reader.result.split(',')[1];
          
          const response = await api.post(`${API}/receipts/scan`, {
            image_base64: base64,
          });
          
          // Fill in the extracted data
          if (response.data.merchant) setMerchant(response.data.merchant);
          if (response.data.date) setDate(response.data.date);
          if (response.data.amount) setAmount(response.data.amount.toString());
          
          toast.success('Receipt scanned! Please verify the information.');
        } catch (error) {
          console.error('Scan error:', error);
          toast.error('Failed to scan receipt. Please enter details manually.');
        } finally {
          setScanning(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error reading file:', error);
      toast.error('Failed to read image');
      setScanning(false);
    }
  };

  const onDrop = (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setImageFile(file);
      // Automatically scan the receipt
      scanReceipt(file);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.png', '.jpg', '.jpeg'] },
    multiple: false,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!imageFile || !amount) {
      toast.error('Please upload receipt and enter amount');
      return;
    }

    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result.split(',')[1];
        
        await api.post(`${API}/receipts`, {
          date,
          amount: parseFloat(amount),
          merchant,
          notes,
          image_base64: base64,
        });
        
        toast.success('Receipt uploaded successfully');
        onClose();
      };
      reader.readAsDataURL(imageFile);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to upload receipt');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="glass-card p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold">Upload Fuel Receipt</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Upload Area First */}
          <div>
            <label className="block text-sm font-medium mb-2">Receipt Image</label>
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-xl p-6 transition-colors cursor-pointer ${
                isDragActive
                  ? 'border-[#14B8A6] bg-[rgba(20,184,166,0.1)]'
                  : imageFile
                  ? 'border-[#14B8A6]'
                  : 'border-[rgba(20,184,166,0.3)]'
              }`}
            >
              <input {...getInputProps()} />
              <div className="text-center">
                {scanning ? (
                  <>
                    <p className="text-[#14B8A6] font-medium mb-2">🔍 Scanning...</p>
                    <p className="text-sm text-gray-400">Please wait</p>
                  </>
                ) : imageFile ? (
                  <>
                    <p className="text-[#14B8A6] font-medium mb-2">✓ Receipt Scanned</p>
                    <p className="text-sm text-gray-400">{imageFile.name}</p>
                  </>
                ) : (
                  <>
                    <p className="font-medium mb-2">Upload receipt image</p>
                    <p className="text-sm text-gray-400">Click or drag to upload</p>
                    <p className="text-xs text-gray-500 mt-2">Will auto-scan for details</p>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Editable Fields */}
          {imageFile && !scanning && (
            <>
              <div className="pt-2">
                <p className="text-sm text-[#14B8A6] mb-3">✓ Review and edit extracted information:</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Merchant Name</label>
                <input
                  type="text"
                  value={merchant}
                  onChange={(e) => setMerchant(e.target.value)}
                  className="glass-input w-full text-sm"
                  placeholder="e.g. Shell, Tesco"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Date</label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="glass-input w-full text-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Amount (\u00a3)</label>
                <input
                  type="number"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="glass-input w-full text-sm"
                  placeholder="45.50"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Notes (Optional)</label>
                <input
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="glass-input w-full text-sm"
                  placeholder="Additional notes"
                />
              </div>

              <button
                type="submit"
                disabled={uploading}
                className="btn-primary w-full text-sm"
              >
                {uploading ? 'Saving...' : 'Save Receipt'}
              </button>
            </>
          )}
        </form>
      </div>
    </div>
  );
};

export default Dashboard;
